import Skeleton from '@mui/material/Skeleton';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { CharacterTabStrip, type CharacterTabItem } from '../components/bosses/CharacterTabStrip';
import { HexaSection, HexaSectionSkeleton } from '../components/goals/HexaSection';
import { HexaOverview, type HexaOverviewCharacter } from '../components/hexa/HexaOverview';
import { useHexaForAccount } from '../lib/hexa';
import { isErdaLinkClass } from '../lib/hexaOverview';
import { avatarProxyPath, useAccounts } from '../lib/roster';
import { classDisplayName, useClasses } from '../lib/reference';

/**
 * `/hexa` — the account-wide HEXA tracker (REV20 — docs/specs/REV20-hexa-page.md). Same
 * character-tab-strip + trailing "Overview" tab pattern as `/pitched`: `Pitched.tsx` is the page
 * template this mirrors almost verbatim (`?account=`/`?character=` resolution, `CharacterTabStrip`,
 * Overview branch, guards). The character tab body reuses `HexaSection` (REV9's per-character node
 * editor) wholesale, plus an "Erda Link — not tracked" badge above it for classes whose sixth-job
 * system isn't HEXA at all.
 */
/** Whole-page loading placeholder mirroring the real layout (header card, tab strip, section) so
 *  nothing reflows when the roster lands. MUI Skeleton per the app's loading-state direction. */
function HexaPageSkeleton() {
  const sx = { bgcolor: 'var(--bg-elevated-hover)' } as const;
  return (
    <div className="space-y-5" aria-hidden="true">
      <div className="space-y-2 rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
        <Skeleton variant="text" width={96} sx={{ ...sx, fontSize: '1.25rem' }} />
        <Skeleton variant="text" width={320} sx={{ ...sx, fontSize: '0.875rem' }} />
      </div>
      <div className="flex gap-2">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" width={128} height={40} sx={{ ...sx, borderRadius: '0.5rem' }} />
        ))}
      </div>
      <HexaSectionSkeleton />
    </div>
  );
}

export function Hexa() {
  const [searchParams, setSearchParams] = useSearchParams();

  const accountsQuery = useAccounts();
  const classesQuery = useClasses();
  const classes = classesQuery.data ?? [];
  const accounts = accountsQuery.data ?? [];

  // Same requested -> valid-set -> first-account fallback as Pitched.tsx/Bosses.tsx, so the
  // command palette's `hexa <character>` (which emits `?account=&character=`) always lands
  // correctly even across accounts.
  const accountParam = searchParams.get('account');
  const requestedAccountId = accountParam !== null ? Number(accountParam) : null;
  const validAccountIds = new Set(accounts.map((a) => a.id));
  const effectiveAccountId =
    requestedAccountId !== null && validAccountIds.has(requestedAccountId)
      ? requestedAccountId
      : (accounts[0]?.id ?? null);

  const selectAccount = (id: number) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('account', String(id));
      return next;
    });
  };

  const hexaQuery = useHexaForAccount(effectiveAccountId);

  if (accountsQuery.isPending || classesQuery.isPending) {
    return <HexaPageSkeleton />;
  }
  if (accountsQuery.isError || classesQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load the HEXA tracker. Please retry in a moment.
      </p>
    );
  }
  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        note="Add your main under Characters and let's start tracking HEXA."
        actionTo="/characters"
        actionLabel="Go to Characters"
      />
    );
  }

  const effectiveAccount = accounts.find((a) => a.id === effectiveAccountId);
  const rosterCharacters = effectiveAccount?.characters ?? [];
  const nodesByCharacter = new Map((hexaQuery.data ?? []).map((c) => [c.characterId, c.nodes]));
  const statNodesByCharacter = new Map((hexaQuery.data ?? []).map((c) => [c.characterId, c.statNodes]));

  const tabs: CharacterTabItem[] = [
    ...rosterCharacters.map((c) => ({
      id: c.id,
      label: c.name,
      microtext: `${classDisplayName(c.classSlug, classes)} · Lv.${c.level}`,
      avatarUrl: c.avatarUrl,
      accountId: c.accountId,
    })),
    { id: 'overview' as const, label: 'Overview' },
  ];
  const validTabIds = new Set<number | 'overview'>(tabs.map((t) => t.id));

  const characterParam = searchParams.get('character');
  const requestedTabId: number | 'overview' | null =
    characterParam === 'overview' ? 'overview' : characterParam !== null ? Number(characterParam) : null;
  const selectedTabId: number | 'overview' =
    requestedTabId !== null && validTabIds.has(requestedTabId)
      ? requestedTabId
      : (rosterCharacters[0]?.id ?? 'overview');

  const selectTab = (id: number | 'overview') => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('character', String(id));
      return next;
    });
  };

  const overviewCharacters: HexaOverviewCharacter[] = rosterCharacters.map((c) => ({
    characterId: c.id,
    name: c.name,
    avatarSrc: c.avatarUrl != null ? avatarProxyPath(c.accountId, c.id) : null,
    classSlug: c.classSlug,
    level: c.level,
    nodes: nodesByCharacter.get(c.id) ?? [],
    statNodes: statNodesByCharacter.get(c.id) ?? [],
  }));

  const selectedCharacter = rosterCharacters.find((c) => c.id === selectedTabId);
  const selectedIsErdaLink = selectedCharacter != null && isErdaLinkClass(selectedCharacter.classSlug, classes);

  return (
    <div className="stagger-children space-y-5">
      <div className="space-y-4 rounded-xl border border-border bg-bg-elevated p-4 sm:p-5">
        <div>
          <h1 className="font-display text-xl font-semibold text-text">HEXA</h1>
          <p className="mt-1 text-sm text-text-muted">
            Sixth Job HEXA Matrix progress — nodes, Sol Erda, and fragments, per character.
          </p>
        </div>

        {accounts.length > 1 && (
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Account">
            {accounts.map((account) => (
              <button
                key={account.id}
                type="button"
                role="tab"
                aria-selected={account.id === effectiveAccountId}
                onClick={() => selectAccount(account.id)}
                className={[
                  'min-h-tap rounded-full px-4 text-sm font-semibold transition-colors',
                  account.id === effectiveAccountId
                    ? 'bg-accent text-accent-contrast'
                    : 'bg-bg-elevated-hover text-text-muted hover:bg-border-strong',
                ].join(' ')}
              >
                {account.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <CharacterTabStrip tabs={tabs} selectedId={selectedTabId} onSelect={selectTab} />

      {hexaQuery.isPending ? (
        selectedTabId === 'overview' ? (
          <div className="space-y-3" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={132}
                sx={{ bgcolor: 'var(--bg-elevated-hover)', borderRadius: '0.75rem' }}
              />
            ))}
          </div>
        ) : (
          <HexaSectionSkeleton />
        )
      ) : hexaQuery.isError ? (
        <p role="alert" className="text-sm text-danger">
          Could not load HEXA nodes. Please retry in a moment.
        </p>
      ) : selectedTabId === 'overview' ? (
        <div id="hexa-tabpanel-overview" role="tabpanel" aria-labelledby="hexa-tab-overview">
          <HexaOverview characters={overviewCharacters} classes={classes} />
        </div>
      ) : (
        <div className="space-y-3">
          {selectedIsErdaLink && (
            <span className="inline-flex items-center rounded-full bg-bg-elevated-hover px-3 py-1 text-xs font-semibold uppercase tracking-wider text-text-faint">
              Erda Link — not tracked
            </span>
          )}
          <HexaSection characterId={selectedTabId} classSlug={selectedCharacter?.classSlug ?? ''} />
        </div>
      )}
    </div>
  );
}
